import { useCallback, useRef, useState } from 'react';
import { Upload, X, RotateCcw, Loader2, FileImage, CheckCircle2 } from 'lucide-react';

export interface AssetUploadFieldProps {
  /** Label shown above the field */
  label: string;
  /** File name of the current custom asset (extracted from S3 key or upload) */
  fileName?: string;
  /** Whether a global default exists for this asset */
  hasDefault: boolean;
  /** Whether this field has a project-specific override */
  isOverridden: boolean;
  /** Accepted MIME types (e.g., "image/png,image/svg+xml") */
  accept?: string;
  /** Upload handler — receives file, returns the new URL */
  onUpload: (file: File) => Promise<string>;
  /** Remove/delete handler for the current override */
  onRemove: () => Promise<void>;
}

export function AssetUploadField({
  label,
  fileName,
  hasDefault,
  isOverridden,
  accept = 'image/png,image/jpeg,image/svg+xml,image/webp,image/gif,image/x-icon,image/vnd.microsoft.icon',
  onUpload,
  onRemove,
}: AssetUploadFieldProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayFileName = uploadedFileName || fileName;

  const handleFile = useCallback(async (file: File) => {
    setError('');
    setIsUploading(true);
    try {
      await onUpload(file);
      setUploadedFileName(file.name);
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleFile]);

  const handleRemove = useCallback(async () => {
    setError('');
    setIsRemoving(true);
    try {
      await onRemove();
      setUploadedFileName(null);
    } catch {
      setError('Failed to remove. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  }, [onRemove]);

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      <div
        className={`rounded-lg border transition-colors ${
          isDragOver
            ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
            : isOverridden
              ? 'border-primary-200 bg-primary-50/50 dark:border-primary-800 dark:bg-primary-900/10'
              : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
        } ${isUploading || isRemoving ? 'pointer-events-none opacity-60' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex items-center gap-3 px-3 py-2.5">
          {/* Status indicator */}
          {isOverridden ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-primary-500 dark:text-primary-400" />
          ) : (
            <FileImage className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          )}

          {/* Text content */}
          <div className="flex-1 min-w-0">
            {isUploading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Uploading...</span>
              </div>
            ) : isRemoving ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Removing...</span>
              </div>
            ) : isOverridden ? (
              <p className="text-sm text-gray-900 dark:text-white truncate">
                {displayFileName || 'Custom asset'}
              </p>
            ) : hasDefault ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Using global default
              </p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No asset configured
              </p>
            )}
          </div>

          {/* Actions */}
          {!isUploading && !isRemoving && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <Upload className="h-3.5 w-3.5" />
                {isOverridden ? 'Replace' : 'Upload'}
              </button>
              {isOverridden && (
                <button
                  type="button"
                  onClick={handleRemove}
                  title={hasDefault ? 'Reset to default' : 'Remove'}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  {hasDefault ? (
                    <RotateCcw className="h-3.5 w-3.5" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
